import { fillRectInnerBorder } from "../helpers/canvas-helpers";
import { makeFont } from "../helpers/make-font";
import { BitmapCoordinatesPaneRenderer } from "./bitmap-coordinates-pane-renderer";
import { drawHorizontalLine } from "./draw-line";
import { optimalCandlestickWidth } from "./optimal-bar-width";
;
export class PaneRendererClusters extends BitmapCoordinatesPaneRenderer {
    constructor() {
        super(...arguments);
        this._private__data = null;
        // scaled with pixelRatio
        this._private__barWidth = 0;
    }
    _internal_setData(data) {
        this._private__data = data;
    }
    _internal__drawImpl(renderingScope) {
        if (this._private__data === null ||
            this._private__data._internal_bars.length === 0 ||
            this._private__data._internal_visibleRange === null) {
            return;
        }
        const { horizontalPixelRatio } = renderingScope;
        // now we know pixelRatio and we could calculate barWidth effectively
        this._private__barWidth = optimalCandlestickWidth(this._private__data._internal_barSpacing, horizontalPixelRatio);
        // grid and crosshair have line width = Math.floor(pixelRatio)
        // if this value is odd, we have to make Clusters' width odd
        // if this value is even, we have to make Clusters' width even
        // in order of keeping crosshair-over-Clusters drawing symmetric
        if (this._private__barWidth >= 2) {
            const wickWidth = Math.floor(horizontalPixelRatio);
            if (wickWidth % 2 !== this._private__barWidth % 2) {
                this._private__barWidth--;
            }
        }
        const bars = this._private__data._internal_bars;
        if (this._private__data._internal_wickVisible) {
            this._private__drawWicks(renderingScope, bars, this._private__data._internal_visibleRange);
        }
        if (this._private__data._internal_borderVisible) {
            this._private__drawBorder(renderingScope, bars, this._private__data._internal_visibleRange);
        }
        const borderWidth = this._private__calculateBorderWidth(horizontalPixelRatio);
        if (!this._private__data._internal_borderVisible || this._private__barWidth > borderWidth * 2) {
            this._private__drawCluster(renderingScope, bars, this._private__data._internal_visibleRange);
        }
    }
    _private__calculateBorderWidth(pixelRatio) {
        let borderWidth = Math.floor(1 /* Constants.BarBorderWidth */ * pixelRatio);
        if (this._private__barWidth <= 2 * borderWidth) {
            borderWidth = Math.floor((this._private__barWidth - 1) * 0.5);
        }
        const res = Math.max(Math.floor(pixelRatio), borderWidth);
        if (this._private__barWidth <= res * 2) {
            // do not draw bodies, restore original value
            return Math.max(Math.floor(pixelRatio), Math.floor(1 /* Constants.BarBorderWidth */ * pixelRatio));
        }
        return res;
    }
    _private__drawBorder(renderingScope, bars, visibleRange) {
        if (this._private__data === null) {
            return;
        }
        const { context: ctx, horizontalPixelRatio, verticalPixelRatio, } = renderingScope;
        let prevBorderColor = "";
        const borderWidth = this._private__calculateBorderWidth(horizontalPixelRatio);
        let prevEdge = null;
        for (let i = visibleRange.from; i < visibleRange.to; i++) {
            const bar = bars[i];
            if (bar._internal_barBorderColor !== prevBorderColor) {
                ctx.fillStyle = bar._internal_barBorderColor;
                prevBorderColor = bar._internal_barBorderColor;
            }
            let left = Math.round(bar._internal_x * horizontalPixelRatio) -
                Math.floor(this._private__barWidth * 0.5);
            // this is important to calculate right before patching left
            const right = left + this._private__barWidth - 1;
            const top = Math.round(Math.min(bar._internal_openY, bar._internal_closeY) * verticalPixelRatio);
            const bottom = Math.round(Math.max(bar._internal_openY, bar._internal_closeY) * verticalPixelRatio);
            if (prevEdge !== null) {
                left = Math.max(prevEdge + 1, left);
                left = Math.min(left, right);
            }
            if (this._private__data._internal_barSpacing * horizontalPixelRatio > 2 * borderWidth) {
                fillRectInnerBorder(ctx, left, top, right - left + 1, bottom - top + 1, borderWidth);
            }
            else {
                const width = right - left + 1;
                ctx.fillRect(left, top, width, bottom - top + 1);
            }
            prevEdge = right;
        }
    }
    _private__drawCluster(renderingScope, bars, visibleRange) {
        if (this._private__data === null) {
            return;
        }
        const { context: ctx, horizontalPixelRatio, verticalPixelRatio, } = renderingScope;
        let prevBarColor = "";
        for (let i = visibleRange.from; i < visibleRange.to; i++) {
            const bar = bars[i];
            const top = Math.round(bar._internal_highY * verticalPixelRatio);
            const bottom = Math.round(bar._internal_lowY * verticalPixelRatio);
            const left = Math.round(bar._internal_x * horizontalPixelRatio) -
                Math.floor(this._private__barWidth * 0.5);
            const right = left + this._private__barWidth;
            if (bar._internal_barColor !== prevBarColor) {
                const barColor = bar._internal_barColor;
                ctx.fillStyle = barColor;
                prevBarColor = barColor;
            }
            if (top > bottom) {
                continue;
            }
            const fontSize = right - left;
            if (fontSize > 10) {
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.font = makeFont(Math.min(fontSize, 16));
                ctx.fillStyle = "#000";
                const tickSize = 10;
                const clusterValueBottom = bar._internal_low;
                let clusterValueTop = this._private__calculatePriceStartBasedOnTickSize(bar._internal_high, tickSize);
                let posY = this._private__convertPriceToPx(clusterValueTop, top, bottom, bar);
                const nextPosY = this._private__convertPriceToPx(clusterValueTop - tickSize, top, bottom, bar);
                const delta = this._private__calculateDeltaValue(bar, clusterValueTop, clusterValueTop - tickSize);
                if (delta !== 0) {
                    drawHorizontalLine(ctx, posY, left, right);
                }
                const textHCenter = Math.round((right - left) / 2);
                const textVCenter = Math.round((nextPosY - posY) / 2);
                while (clusterValueTop > clusterValueBottom) {
                    const nextTickValue = clusterValueTop - tickSize;
                    const delta = this._private__calculateDeltaValue(bar, clusterValueTop, nextTickValue);
                    const lastPosY = posY;
                    posY = this._private__convertPriceToPx(nextTickValue, top, bottom, bar);
                    if (delta !== 0) {
                        ctx.fillStyle = "#ccc";
                        ctx.fillRect(left, lastPosY + 1, this._private__barWidth, posY - lastPosY);
                        ctx.fillStyle = delta > 0 ? "#383" : "#F55";
                        ctx.fillText(this._private__format(delta), left + textHCenter, posY - textVCenter);
                        // ctx.fillStyle = delta > 0 ? "#050" : "#F00";
                        // fillRectInnerBorder(
                        // 	ctx,
                        // 	left,
                        // 	lastPosY,
                        // 	right - left + 1,
                        // 	posY - lastPosY + 1,
                        // 	1
                        // );
                        drawHorizontalLine(ctx, posY, left, right);
                    }
                    clusterValueTop = nextTickValue;
                }
            }
        }
    }
    _private__calculatePriceStartBasedOnTickSize(price, tick) {
        return Math.round(Math.ceil(price / tick) * tick);
    }
    _private__convertPriceToPx(price, top, bottom, bar) {
        return Math.round(top + ((top - bottom) * (price - bar._internal_high)) / (bar._internal_high - bar._internal_low));
    }
    _private__calculateDeltaValue(bar, high, low) {
        let sum = 0;
        bar._internal_trades.forEach((t) => {
            if (t.price > low && t.price <= high) {
                sum += Number(t.delta);
            }
        });
        return sum;
    }
    _private__format(price) {
        if (Math.abs(price) < 1000) {
            return price.toString();
        }
        const si = [
            { _internal_v: 1e3, _internal_s: "K" },
            { _internal_v: 1e6, _internal_s: "M" },
            { _internal_v: 1e9, _internal_s: "B" },
            { _internal_v: 1e12, _internal_s: "T" },
            { _internal_v: 1e15, _internal_s: "P" },
            { _internal_v: 1e18, _internal_s: "E" },
        ];
        let index;
        for (index = si.length - 1; index > 0; index--) {
            if (price >= si[index]._internal_v) {
                break;
            }
        }
        return ((price / si[index]._internal_v)
            .toFixed(0)
            .replace(/\.0+$|(\.[0-9]*[1-9])0+$/, "$1") + si[index]._internal_s);
    }
    _private__drawWicks(renderingScope, bars, visibleRange) {
        if (this._private__data === null) {
            return;
        }
        const { context: ctx, horizontalPixelRatio, verticalPixelRatio, } = renderingScope;
        let prevWickColor = "";
        let wickWidth = Math.min(Math.floor(horizontalPixelRatio), Math.floor(this._private__data._internal_barSpacing * horizontalPixelRatio));
        wickWidth = Math.max(Math.floor(horizontalPixelRatio), Math.min(wickWidth, this._private__barWidth));
        const wickOffset = Math.floor(wickWidth * 0.5);
        let prevEdge = null;
        for (let i = visibleRange.from; i < visibleRange.to; i++) {
            const bar = bars[i];
            if (bar._internal_barWickColor !== prevWickColor) {
                ctx.fillStyle = bar._internal_barWickColor;
                prevWickColor = bar._internal_barWickColor;
            }
            const top = Math.round(Math.min(bar._internal_openY, bar._internal_closeY) * verticalPixelRatio);
            const bottom = Math.round(Math.max(bar._internal_openY, bar._internal_closeY) * verticalPixelRatio);
            const high = Math.round(bar._internal_highY * verticalPixelRatio);
            const low = Math.round(bar._internal_lowY * verticalPixelRatio);
            const scaledX = Math.round(horizontalPixelRatio * bar._internal_x);
            let left = scaledX - wickOffset;
            const right = left + wickWidth - 1;
            if (prevEdge !== null) {
                left = Math.max(prevEdge + 1, left);
                left = Math.min(left, right);
            }
            const width = right - left + 1;
            ctx.fillRect(left, high, width, top - high);
            ctx.fillRect(left, bottom + 1, width, low - bottom);
            prevEdge = right;
        }
    }
}
